import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { IconPlus } from '@tabler/icons-react';
import {
  DocumentSnapshot,
  getDocs,
  limit,
  orderBy,
  query,
  QueryConstraint,
  startAfter,
  where,
} from 'firebase/firestore';
import { map } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import NotaDb from '../../../../db/notaDb';
import { NotaBovespa, NotaBMF } from '../../../../models/nota';
import { useUsuario } from '../../../../providers/usuarioProvider';
import TabelaNotas from '../../../util/tabelas/tabelaNotas';
import CorretoraSelect from '../../../util/ui/corretoraSelect';
import PeriodoSelect from '../../../util/ui/periodoSelect';
import Corretora from '../../../../models/corretora';
import { Link } from 'react-router-dom';
import { SituacaoNota } from '../../../../models/enum';

const ITENS_POR_PAGINA = 20;
const PERIODO_PADRAO_INICIO = new Date(new Date().getFullYear(), 0, 1, 0, 0, 0);
const PERIODO_PADRAO_FIM = new Date(
  new Date().getFullYear(),
  11,
  31,
  23,
  59,
  59,
);

function resetState(
  setNotas: React.Dispatch<React.SetStateAction<(NotaBovespa | NotaBMF)[]>>,
  setUltimoDoc: React.Dispatch<React.SetStateAction<DocumentSnapshot | null>>,
  setHasMore: React.Dispatch<React.SetStateAction<boolean>>,
) {
  setNotas([]);
  setUltimoDoc(null);
  setHasMore(true);
}

export default function AbaNotas() {
  const { visualizado } = useUsuario();
  const [notas, setNotas] = useState<(NotaBovespa | NotaBMF)[]>([]);
  const [corretoraSelecionada, setCorretoraSelecionada] = useState<
    Corretora | undefined
  >();
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [ultimoDoc, setUltimoDoc] = useState<DocumentSnapshot | null>(null);
  const [dataInicio, setDataInicio] = useState<Date | undefined>(
    PERIODO_PADRAO_INICIO,
  );
  const [dataFim, setDataFim] = useState<Date | undefined>(PERIODO_PADRAO_FIM);
  const [statusSelecionado, setStatusSelecionado] = useState<
    SituacaoNota | undefined
  >();
  const loaderRef = useRef<HTMLDivElement | null>(null);

  const montarConstraints = useCallback(
    (usarCursor?: DocumentSnapshot | null) => {
      const constraints: QueryConstraint[] = [];

      if (corretoraSelecionada) {
        constraints.push(where('corretora', '==', corretoraSelecionada?.id));
      }
      if (dataInicio) {
        constraints.push(where('dataPregao', '>=', dataInicio));
      }
      if (dataFim) {
        constraints.push(where('dataPregao', '<=', dataFim));
      }
      if (statusSelecionado) {
        constraints.push(where('situacao', '==', statusSelecionado));
      }

      constraints.push(orderBy('dataPregao', 'desc'));

      if (usarCursor) {
        constraints.push(startAfter(usarCursor));
      }

      constraints.push(limit(ITENS_POR_PAGINA));

      return constraints;
    },
    [corretoraSelecionada, dataInicio, dataFim, statusSelecionado],
  );

  // Carga inicial / reset quando filtros mudam
  useEffect(() => {
    if (!visualizado) return;

    setLoading(true);

    const constraints = montarConstraints(null);

    getDocs(
      query(
        new NotaDb().collection({ usuarioId: visualizado, notaId: '' }),
        ...constraints,
      ),
    ).then((snapshot) => {
      setNotas(map(snapshot.docs, (doc) => doc.data()));
      setUltimoDoc(
        snapshot.docs.length > 0
          ? snapshot.docs[snapshot.docs.length - 1]
          : null,
      );
      setHasMore(snapshot.docs.length === ITENS_POR_PAGINA);
      setLoading(false);
    });
  }, [
    visualizado,
    corretoraSelecionada,
    dataInicio,
    dataFim,
    statusSelecionado,
    montarConstraints,
  ]);

  const carregarMaisItens = useCallback(() => {
    if (!hasMore || !visualizado || loading || !ultimoDoc) return;

    setLoading(true);

    const constraints = montarConstraints(ultimoDoc);

    getDocs(
      query(
        new NotaDb().collection({ usuarioId: visualizado, notaId: '' }),
        ...constraints,
      ),
    ).then((snapshot) => {
      const novasNotas = map(snapshot.docs, (doc) => doc.data());
      setNotas((prev) => [...prev, ...novasNotas]);
      setUltimoDoc(
        snapshot.docs.length > 0
          ? snapshot.docs[snapshot.docs.length - 1]
          : null,
      );
      setHasMore(snapshot.docs.length === ITENS_POR_PAGINA);
      setLoading(false);
    });
  }, [hasMore, visualizado, loading, ultimoDoc, montarConstraints]);

  // Quando qualquer filtro muda, reseta o estado da paginação
  const handleCorretoraChange = (corretora?: Corretora) => {
    setCorretoraSelecionada(corretora);
    resetState(setNotas, setUltimoDoc, setHasMore);
  };

  const handleStatusChange = (status?: SituacaoNota) => {
    setStatusSelecionado(status);
    resetState(setNotas, setUltimoDoc, setHasMore);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          carregarMaisItens();
        }
      },
      { threshold: 1.0 },
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [hasMore, loading, carregarMaisItens]);

  return (
    <Box display={'flex'} flexDirection="column">
      <Box
        display="flex"
        width="100%"
        flexDirection="row"
        justifyContent="space-between"
        pb={2}
        gap={2}
        flexWrap="wrap"
        alignItems="center"
      >
        <PeriodoSelect
          dataInicio={dataInicio}
          dataFim={dataFim}
          onChange={(inicio, fim) => {
            setDataInicio(inicio);
            setDataFim(fim);
            resetState(setNotas, setUltimoDoc, setHasMore);
          }}
        />
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to="/arquivo/nota/nova"
          startIcon={<IconPlus />}
        >
          Nota manual
        </Button>
      </Box>
      <Box
        display="flex"
        width="100%"
        flexDirection="row"
        pb={2}
        gap={2}
        flexWrap="wrap"
        alignItems="center"
      >
        <CorretoraSelect
          value={corretoraSelecionada}
          onSelect={handleCorretoraChange}
        />
        <FormControl size="small" sx={{ width: '20ch' }}>
          <InputLabel shrink>Status</InputLabel>
          <Select
            label="Status"
            value={statusSelecionado ?? ''}
            displayEmpty
            notched
            onChange={(e) => {
              handleStatusChange(
                e.target.value ? (e.target.value as SituacaoNota) : undefined,
              );
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value={SituacaoNota.Nova}>Nova</MenuItem>
            <MenuItem value={SituacaoNota.Obsoleta}>Obsoleta</MenuItem>
            <MenuItem value={SituacaoNota.Pronta}>Pronta</MenuItem>
            <MenuItem value={SituacaoNota.Inconsistente}>
              Inconsistente
            </MenuItem>
            <MenuItem value={SituacaoNota.Parcial}>Parcial</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <TabelaNotas notas={notas} loading={loading} />
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
        width="100%"
        paddingY={2}
        gap={2}
      >
        {hasMore && (
          <Button
            variant="outlined"
            size="small"
            onClick={carregarMaisItens}
            disabled={loading}
          >
            {loading
              ? 'Carregando...'
              : `Carregar mais ${ITENS_POR_PAGINA} notas`}
          </Button>
        )}
        {/* Contador é dado: mono tabular para não pular a cada página nova. */}
        <Typography
          variant="labelMono"
          color="text.secondary"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          Mostrando {notas.length} nota(s)
        </Typography>
        {hasMore && <div ref={loaderRef} style={{ height: 1 }} />}
      </Box>
    </Box>
  );
}
